import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Printer, Plus, Download, RefreshCw, Trash2, Loader2,
  Wifi, WifiOff, Copy, Eye, EyeOff, KeyRound, Server, TestTube2, RotateCcw, History, Route,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  adminListAgents, adminCreateAgent, adminRotateKey, adminDeleteAgent, adminDownloadAgent,
  adminTestPrint, adminListJobs, adminReprintJob, getPrintJob,
  adminListOperationRoutes, adminSetOperationRoute,
} from '@/services/networkPrintApi';

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
};

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700 border-amber-300',
  printing: 'bg-blue-100 text-blue-700 border-blue-300',
  printed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  failed: 'bg-red-100 text-red-700 border-red-300',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-300',
};

// ---------- Stations tab ----------
const StationsTab = ({ agents, loading, load }) => {
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [revealed, setRevealed] = useState({});
  const [testPrinter, setTestPrinter] = useState({});

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Enter a station name'); return; }
    setCreating(true);
    try {
      await adminCreateAgent(newName.trim());
      toast.success(`Station "${newName.trim()}" created`);
      setNewName('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create station');
    } finally { setCreating(false); }
  };

  const handleDownload = async (a) => {
    setBusyId(a.agent_id);
    try {
      await adminDownloadAgent(a.agent_id, a.name);
      toast.success('Agent downloaded. Unzip it on the printer PC and run run_agent.bat');
    } catch (e) { toast.error('Failed to download agent'); }
    finally { setBusyId(''); }
  };

  const handleRotate = async (a) => {
    if (!window.confirm(`Rotate the key for "${a.name}"? Re-download the agent afterwards.`)) return;
    setBusyId(a.agent_id);
    try { await adminRotateKey(a.agent_id); toast.success('Key rotated. Re-download the agent.'); load(); }
    catch (e) { toast.error('Failed to rotate key'); }
    finally { setBusyId(''); }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete station "${a.name}"? Pending jobs will be cancelled.`)) return;
    setBusyId(a.agent_id);
    try { await adminDeleteAgent(a.agent_id); toast.success('Station deleted'); load(); }
    catch (e) { toast.error('Failed to delete station'); }
    finally { setBusyId(''); }
  };

  const handleTest = async (a) => {
    const printer = testPrinter[a.agent_id] || a.default_printer || (a.printers || [])[0];
    if (!printer) { toast.error('No printer on this station'); return; }
    setBusyId(a.agent_id);
    try {
      const { job_id } = await adminTestPrint(a.agent_id, printer);
      toast.message(`Test label sent to ${printer}. Waiting for confirmation…`);
      // poll the job briefly for a friendly result
      const start = Date.now();
      let done = false;
      while (Date.now() - start < 15000 && !done) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 1500));
        // eslint-disable-next-line no-await-in-loop
        const j = await getPrintJob(job_id).catch(() => null);
        if (j && j.status === 'printed') { toast.success(`Test print OK on ${printer}`); done = true; }
        else if (j && j.status === 'failed') { toast.error(`Test print failed: ${j.error || 'printer error'}`); done = true; }
      }
      if (!done) toast.message('Test label queued — check the printer.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test print failed');
    } finally { setBusyId(''); }
  };

  const copyKey = (key) => {
    navigator.clipboard?.writeText(key).then(
      () => toast.success('Key copied'),
      () => toast.error('Could not copy'),
    );
  };

  return (
    <div className="space-y-6">
      <Card className="border-[#2D5F4F]/30 bg-[#2D5F4F]/5">
        <CardContent className="py-4 text-sm text-gray-700 space-y-1">
          <p className="flex items-center gap-2 font-medium text-[#2D5F4F]"><Server className="w-4 h-4" /> How it works</p>
          <p>
            Create one station per printer PC. Download that station&apos;s agent, unzip it on the Windows PC
            with the TSC printer, and run <code>run_agent.bat</code>. The station then shows <strong>Online</strong> and its
            printers become selectable in the app (choose &quot;Network&quot;). The agent only makes outbound connections — no firewall changes.
          </p>
        </CardContent>
      </Card>

      <Card data-testid="add-station-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Plus className="w-4 h-4" /> Add a print station</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input placeholder="Station name (e.g. Packing Station 3)" value={newName}
              onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              data-testid="station-name-input" />
            <Button onClick={handleCreate} disabled={creating} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="create-station-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Create
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-700">Stations ({agents.length})</h2>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reload-stations-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {!loading && agents.length === 0 && (
        <div className="text-center py-10 text-gray-500" data-testid="no-stations">No print stations yet. Add one above.</div>
      )}

      <div className="grid gap-4">
        {agents.map((a) => (
          <Card key={a.agent_id} data-testid={`station-card-${a.agent_id}`}>
            <CardContent className="py-4 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-lg">{a.name}</span>
                    {a.online ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300" data-testid={`station-online-${a.agent_id}`}>
                        <Wifi className="w-3 h-3 mr-1" /> Online</Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-500" data-testid={`station-offline-${a.agent_id}`}>
                        <WifiOff className="w-3 h-3 mr-1" /> Offline</Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Last seen: {fmtTime(a.last_seen)}{a.os_info ? ` · ${a.os_info}` : ''}{a.agent_version ? ` · agent v${a.agent_version}` : ''}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" onClick={() => handleDownload(a)} disabled={busyId === a.agent_id}
                    className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid={`download-agent-${a.agent_id}`}>
                    <Download className="w-4 h-4 mr-1" /> Download agent
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleRotate(a)} disabled={busyId === a.agent_id} data-testid={`rotate-key-${a.agent_id}`}>
                    <KeyRound className="w-4 h-4 mr-1" /> Rotate key
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(a)} disabled={busyId === a.agent_id}
                    className="text-red-600 border-red-300 hover:bg-red-50" data-testid={`delete-station-${a.agent_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Detected printers</div>
                {(a.printers || []).length === 0 ? (
                  <span className="text-xs text-gray-400">None yet — start the agent on the printer PC.</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {a.printers.map((p) => (
                      <Badge key={p} variant="outline" className={p === a.default_printer ? 'border-[#2D5F4F] text-[#2D5F4F]' : ''}>
                        <Printer className="w-3 h-3 mr-1" /> {p}{p === a.default_printer ? ' (default)' : ''}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Test print */}
              {a.online && (a.printers || []).length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  {a.printers.length > 1 && (
                    <Select value={testPrinter[a.agent_id] || a.default_printer || a.printers[0]}
                      onValueChange={(v) => setTestPrinter((t) => ({ ...t, [a.agent_id]: v }))}>
                      <SelectTrigger className="h-8 w-56 text-xs" data-testid={`test-printer-select-${a.agent_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {a.printers.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Button size="sm" variant="outline" onClick={() => handleTest(a)} disabled={busyId === a.agent_id}
                    className="text-[#2D5F4F] border-[#2D5F4F]/40" data-testid={`test-print-${a.agent_id}`}>
                    <TestTube2 className="w-4 h-4 mr-1" /> Test print
                  </Button>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs">
                <span className="font-medium text-gray-500">Key:</span>
                <code className="bg-gray-100 px-2 py-1 rounded font-mono">
                  {revealed[a.agent_id] ? a.api_key : '••••••••••••••••'}
                </code>
                <button type="button" onClick={() => setRevealed((r) => ({ ...r, [a.agent_id]: !r[a.agent_id] }))}
                  className="text-gray-500 hover:text-gray-800" data-testid={`toggle-key-${a.agent_id}`}>
                  {revealed[a.agent_id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button type="button" onClick={() => copyKey(a.api_key)} className="text-gray-500 hover:text-gray-800" data-testid={`copy-key-${a.agent_id}`}>
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ---------- Routing tab (operation → station) ----------
const RoutingTab = ({ agents }) => {
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListOperationRoutes();
      setOps(data.operations || []);
    } catch (e) {
      toast.error('Failed to load operation routing');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build the station+printer options from every station's printers.
  const options = [];
  agents.forEach((a) => {
    (a.printers || []).forEach((p) => {
      options.push({ value: `${a.agent_id}::${p}`, label: `${a.name} — ${p}`, online: a.online });
    });
  });

  const handleChange = async (op, v) => {
    setSavingKey(op.operation_key);
    try {
      if (v === 'none') {
        await adminSetOperationRoute(op.operation_key, {});
        toast.success(`Cleared station for "${op.label}"`);
      } else {
        const [agentId, printerName] = v.split('::');
        await adminSetOperationRoute(op.operation_key, { agentId, printerName });
        toast.success(`"${op.label}" now prints to the selected station`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save routing');
    } finally { setSavingKey(''); }
  };

  // Group operations by their `group` for readability.
  const groups = ops.reduce((acc, o) => { (acc[o.group] = acc[o.group] || []).push(o); return acc; }, {});

  return (
    <div className="space-y-6">
      <Card className="border-[#2D5F4F]/30 bg-[#2D5F4F]/5">
        <CardContent className="py-4 text-sm text-gray-700 space-y-1">
          <p className="flex items-center gap-2 font-medium text-[#2D5F4F]"><Route className="w-4 h-4" /> Operation routing</p>
          <p>
            Assign each operation to a fixed print station. Every handheld doing that operation will print to the
            same station automatically — no per-device setup. Leave one as <strong>Not assigned</strong> to let
            workers pick a printer manually on that screen.
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-700">Operations ({ops.length})</h2>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reload-routes-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {options.length === 0 && (
        <div className="text-center py-6 text-amber-600 text-sm" data-testid="no-printers-hint">
          No station printers detected yet. Start an agent on a printer PC first, then assign operations here.
        </div>
      )}

      {Object.entries(groups).map(([group, list]) => (
        <div key={group} className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{group}</h3>
          <div className="grid gap-2">
            {list.map((op) => {
              const value = op.agent_id ? `${op.agent_id}::${op.printer_name}` : 'none';
              const valueMissing = op.agent_id && !options.find((o) => o.value === value);
              return (
                <Card key={op.operation_key} data-testid={`route-row-${op.operation_key}`}>
                  <CardContent className="py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-[200px]">
                      <div className="font-medium text-gray-800">{op.label}</div>
                      {op.agent_id ? (
                        <div className="text-xs mt-0.5 flex items-center gap-1.5">
                          {!op.exists ? (
                            <span className="text-red-600" data-testid={`route-deleted-${op.operation_key}`}>Assigned station was deleted — reassign</span>
                          ) : op.online ? (
                            <span className="text-emerald-600 flex items-center gap-1"><Wifi className="w-3 h-3" /> {op.agent_name} · {op.printer_name}</span>
                          ) : (
                            <span className="text-gray-400 flex items-center gap-1"><WifiOff className="w-3 h-3" /> {op.agent_name} · {op.printer_name} (offline)</span>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400 mt-0.5">Not assigned — workers pick manually</div>
                      )}
                    </div>
                    <Select value={valueMissing ? 'none' : value} onValueChange={(v) => handleChange(op, v)} disabled={savingKey === op.operation_key}>
                      <SelectTrigger className="h-9 w-72" data-testid={`route-select-${op.operation_key}`}>
                        <SelectValue placeholder="Choose a station…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Not assigned —</SelectItem>
                        {options.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}{o.online ? '' : ' (offline)'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------- History tab ----------
const HistoryTab = ({ agents }) => {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [agentId, setAgentId] = useState('all');
  const [status, setStatus] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [skip, setSkip] = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListJobs({
        agentId: agentId === 'all' ? undefined : agentId,
        status: status === 'all' ? undefined : status,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        skip, limit: LIMIT,
      });
      setJobs(data.jobs || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Failed to load print history');
    } finally { setLoading(false); }
  }, [agentId, status, fromDate, toDate, skip]);

  useEffect(() => { load(); }, [load]);

  const handleReprint = async (job) => {
    try {
      await adminReprintJob(job.job_id);
      toast.success('Re-queued for printing');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reprint failed');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="py-4 flex flex-wrap items-end gap-3">
          <div>
            <div className="text-xs text-gray-500 mb-1">Station</div>
            <Select value={agentId} onValueChange={(v) => { setSkip(0); setAgentId(v); }}>
              <SelectTrigger className="h-9 w-52" data-testid="history-station-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stations</SelectItem>
                {agents.map((a) => <SelectItem key={a.agent_id} value={a.agent_id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Status</div>
            <Select value={status} onValueChange={(v) => { setSkip(0); setStatus(v); }}>
              <SelectTrigger className="h-9 w-40" data-testid="history-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="printing">Printing</SelectItem>
                <SelectItem value="printed">Printed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">From</div>
            <Input type="date" value={fromDate} onChange={(e) => { setSkip(0); setFromDate(e.target.value); }} className="h-9 w-40" data-testid="history-from-date" />
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">To</div>
            <Input type="date" value={toDate} onChange={(e) => { setSkip(0); setToDate(e.target.value); }} className="h-9 w-40" data-testid="history-to-date" />
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="history-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </CardContent>
      </Card>

      <div className="text-sm text-gray-500">{total} job(s)</div>

      <div className="overflow-x-auto border rounded-lg bg-white">
        <table className="w-full text-sm" data-testid="print-history-table">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-3 py-2">Time</th>
              <th className="text-left px-3 py-2">Station</th>
              <th className="text-left px-3 py-2">Printer</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">By</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.job_id} className="border-t" data-testid={`history-row-${j.job_id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{fmtTime(j.created_at)}</td>
                <td className="px-3 py-2">{j.agent_name}</td>
                <td className="px-3 py-2">{j.printer_name}</td>
                <td className="px-3 py-2">{j.label_type || '—'}</td>
                <td className="px-3 py-2">{j.created_by_name || '—'}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={STATUS_STYLES[j.status] || ''}>{j.status}</Badge>
                  {j.status === 'failed' && j.error && (
                    <div className="text-xs text-red-500 max-w-xs truncate" title={j.error}>{j.error}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => handleReprint(j)} data-testid={`reprint-${j.job_id}`}>
                    <RotateCcw className="w-4 h-4 mr-1" /> Reprint
                  </Button>
                </td>
              </tr>
            ))}
            {!loading && jobs.length === 0 && (
              <tr><td colSpan="7" className="text-center py-8 text-gray-400" data-testid="history-empty">No print jobs match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} data-testid="history-prev">Previous</Button>
        <span className="text-xs text-gray-500">{skip + 1}–{Math.min(skip + LIMIT, total)} of {total}</span>
        <Button variant="outline" size="sm" disabled={skip + LIMIT >= total} onClick={() => setSkip(skip + LIMIT)} data-testid="history-next">Next</Button>
      </div>
    </div>
  );
};

// ---------- Page shell ----------
const PrintStations = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListAgents();
      setAgents(data.agents || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load print stations');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="print-stations-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="print-stations-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2"><Printer className="w-5 h-5" /> Print Stations</h1>
            <p className="text-sm text-[#E8DCC8]">Network label bridge — print to USB TSC printers from any device</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <Tabs defaultValue="stations">
          <TabsList className="mb-4">
            <TabsTrigger value="stations" data-testid="tab-stations"><Server className="w-4 h-4 mr-1" /> Stations</TabsTrigger>
            <TabsTrigger value="routing" data-testid="tab-routing"><Route className="w-4 h-4 mr-1" /> Operation Routing</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-1" /> Print History</TabsTrigger>
          </TabsList>
          <TabsContent value="stations">
            <StationsTab agents={agents} loading={loading} load={load} />
          </TabsContent>
          <TabsContent value="routing">
            <RoutingTab agents={agents} />
          </TabsContent>
          <TabsContent value="history">
            <HistoryTab agents={agents} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default PrintStations;
